export interface ParsedLeadMining {
  category?: string;
  lastAppSession?: string;
  behaviorSummary?: string;
  intent?: { label: string; value: string }[];
  recommendedLeadType?: string;
  whyThisLeadType?: string;
  journeyNarrative?: string;
  salesRecommendation?: string;
  signals?: string[];
  intentScore?: number;
  extras?: { label: string; value: string }[];
}

const INTENT_LABEL_ORDER = ["unit type", "location", "budget"];

const intentPriority = (label: string) => {
  const normalized = label.trim().toLowerCase();
  if (normalized === "compounds viewed") return Number.MAX_SAFE_INTEGER;
  const idx = INTENT_LABEL_ORDER.indexOf(normalized);
  return idx === -1 ? Number.MAX_SAFE_INTEGER - 1 : idx;
};

const ESCALATION_LABEL_RE = /escalation|urgency/i;

export const splitExtras = (data: ParsedLeadMining) => {
  const escalation = data.extras?.find((e) =>
    ESCALATION_LABEL_RE.test(e.label),
  );
  const followUpGaps =
    data.extras?.filter((e) => !ESCALATION_LABEL_RE.test(e.label)) ?? [];
  return { escalation, followUpGaps };
};

export function parseLeadMining(raw: string): ParsedLeadMining {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const result: ParsedLeadMining = {};
  let i = 0;

  const readBlock = (from: number): { text: string; end: number } => {
    const buf: string[] = [];
    let j = from;
    while (j < lines.length && lines[j].trim() !== "") buf.push(lines[j++]);
    return { text: buf.join("\n").trim(), end: j };
  };

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line.startsWith("Category:")) {
      result.category = line.slice("Category:".length).trim();
    } else if (line.startsWith("Last App Session:")) {
      result.lastAppSession = line.slice("Last App Session:".length).trim();
    } else if (line.startsWith("Recommended Lead Type:")) {
      result.recommendedLeadType = line
        .slice("Recommended Lead Type:".length)
        .trim();
    } else if (line.startsWith("Intent Score:")) {
      const num = parseInt(line.slice("Intent Score:".length).trim(), 10);
      if (!Number.isNaN(num)) result.intentScore = num;
    } else if (line === "Signals:") {
      const bullets: string[] = [];
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== "") {
        const bullet = lines[j].trim().replace(/^-\s*/, "");
        if (bullet) bullets.push(bullet);
        j++;
      }
      if (bullets.length > 0) result.signals = bullets;
      i = j;
      continue;
    } else if (line.startsWith("Why This Lead Type:")) {
      result.whyThisLeadType = line.slice("Why This Lead Type:".length).trim();
    } else if (line === "Behavior Summary:") {
      const block = readBlock(i + 1);
      result.behaviorSummary = block.text;
      i = block.end;
      continue;
    } else if (line === "Inferred Client Intent:") {
      const bullets: { label: string; value: string }[] = [];
      let j = i + 1;
      while (j < lines.length && lines[j].trim() !== "") {
        const bullet = lines[j].trim().replace(/^-\s*/, "");
        const colon = bullet.indexOf(":");
        if (colon > 0) {
          const value = bullet.slice(colon + 1).trim();
          if (value !== "Unknown" && value !== "N/A")
            bullets.push({ label: bullet.slice(0, colon).trim(), value });
        }
        j++;
      }
      result.intent = bullets.sort(
        (a, b) => intentPriority(a.label) - intentPriority(b.label),
      );
      i = j;
      continue;
    } else if (line === "Journey Narrative:") {
      const block = readBlock(i + 1);
      result.journeyNarrative = block.text;
      i = block.end;
      continue;
    } else if (line === "Sales Recommendation:") {
      const block = readBlock(i + 1);
      result.salesRecommendation = block.text;
      i = block.end;
      continue;
    } else {
      const colon = line.indexOf(":");
      if (colon > 0) {
        const label = line.slice(0, colon).trim();
        const value = line.slice(colon + 1).trim();
        if (value) {
          (result.extras ??= []).push({ label, value });
        }
      }
    }

    i++;
  }

  return result;
}
